import './index.scss';
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {ExtensionOutlined, ExtensionRounded} from "@mui/icons-material";
import {Box, Button, Card, CardContent, CardHeader, Container, LinearProgress, Popover, Typography, Unstable_Grid2 as Grid, useMediaQuery} from "@mui/material";
import debounce from "lodash/debounce";
import useIsMounted from "../../../../hooks/use-is-mounted";
import Api from "../../../../../core/services/api/api";
import DateUtils from "../../../../../core/services/date-utils";
import LazyImage from "../../../app-specific/lazy-image";
import BizFileImage from '../../../../../assets/images/art-like/biz-file.png';
import Utils from "../../../../../core/services/utils/utils";
import useAccessLevels from "../../../../hooks/use-access-levels";
import axios from "axios";
import {useTranslation} from "react-i18next";

const usePluginsEndAction = (breakpoint) => {
    const [popoverAnchor, setPopoverAnchor] = useState();
    const passedBreakpoint = useMediaQuery((theme) => theme.breakpoints.up(breakpoint));
    const popoverAction = useRef(null);
    const operatorAccessLevels = useAccessLevels();
    const {t} = useTranslation("components", {keyPrefix: 'panel-sections.app-bar.actions.plugins'});

    const accessToNoPlugins = useMemo(() => {
        return !(
            operatorAccessLevels.packages.bizFile.viewPlugin
        )
    }, [operatorAccessLevels.packages.bizFile.viewPlugin])

    /**
     * With each change in the [passedBreakpoint] value:
     * - Close the popover
     */
    useEffect(() => {
        setPopoverAnchor(undefined)
    }, [passedBreakpoint])

    /**
     * Update the popover position on window resize.
     */
    useEffect(() => {
        if (!popoverAnchor)
            return;
        const debounced = debounce(() => {
            popoverAction.current?.updatePosition()
        }, 1000)
        window.addEventListener('resize', debounced);
        return () => window.removeEventListener('resize', debounced);
    }, [popoverAnchor])

    /**
     * Open the plugins popover
     */
    const openPopover = useCallback((e, anchorElement) => {
        setPopoverAnchor(anchorElement ?? e.currentTarget);
    }, [])

    /**
     * Close the plugins popover
     */
    const closePopover = useCallback(() => {
        setPopoverAnchor(undefined)
    }, [])


    const pluginsPopover = useMemo(() => (
        <Popover
            id={'plugins-popover'}
            action={popoverAction}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
            }}
            slotProps={{root: {sx: {marginBlockStart: 2}}, paper: {sx: (theme) => ({boxShadow: theme.shadows[2]})}}}
            open={!!popoverAnchor}
            anchorEl={popoverAnchor}
            onClose={closePopover}
        >
            <Container
                maxWidth={'sm'}
                className={'plugins-container'}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 2,
                    gap: 2,
                }}
            >
                <Typography variant={'body1'} fontWeight={600}>
                    {t('title')}
                </Typography>
                <Grid container spacing={2}>
                    {
                        operatorAccessLevels.packages.bizFile.viewPlugin &&
                        <Grid xs={12} sm={6}>
                            <BizFilePlugin/>
                        </Grid>
                    }
                </Grid>
            </Container>
        </Popover>
    ), [popoverAnchor, closePopover, t, operatorAccessLevels.packages.bizFile.viewPlugin]);

    return useMemo(() =>
            accessToNoPlugins
                ? null
                : ({
                    key: "plugins",
                    title: t('title'),
                    icon: popoverAnchor ? <ExtensionRounded/> : <ExtensionOutlined/>,
                    color: "primary",
                    onClick: openPopover,
                    children: pluginsPopover,
                }),
        [popoverAnchor, openPopover, pluginsPopover, accessToNoPlugins, t])
}


const BizFilePlugin = () => {
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [data, setData] = useState();
    const isMounted = useIsMounted();
    const {t} = useTranslation("components", {keyPrefix: 'panel-sections.app-bar.actions.plugins.bizFile'});

    const percentageUsed = useMemo(() => !data ? 0 : (data.partnerDiskUsage / data.partnerDiskCapacity * 100), [data])

    /**
     * Get BizFile Plugin Data
     */
    const getBizFilePluginData = useCallback(async (cancelToken) => {
        setLoading(true);
        const response = await Api.getBizFileStorageAccount(cancelToken);
        if (!isMounted() || response?.aborted)
            return;
        setLoading(false);
        if (response?.resultFlag) {
            setData(response.data);
        }
    }, [isMounted])

    /**
     * When component mounted:
     * - Get BizFile Plugin Data
     */
    useEffect(() => {
        const cancelTokenSource = axios.CancelToken.source();
        void getBizFilePluginData(cancelTokenSource.token);
        return () => cancelTokenSource.cancel();
    }, [getBizFilePluginData])

    /**
     * Create BizFile Plugin Data
     */
    const createBizFilePluginData = useCallback(async () => {
        setCreating(true);
        const response = await Api.createBizFileStorageAccount();
        if (!isMounted() || response?.aborted)
            return;
        setCreating(false);
        if (response?.resultFlag) {
            setData(response.data);
        }
    }, [isMounted])

    return (
        <>
            <Card variant={'outlined'}>
                <CardHeader
                    sx={{padding: 1}}
                    title="BizFile"
                    titleTypographyProps={{variant: 'body2', fontWeight: 600}}
                    subheaderTypographyProps={{variant: 'caption'}}
                    subheader={
                        (loading || creating)
                            ? t('fetching')
                            : t('lastUpdated', {date: DateUtils.localNow().format(DateUtils.defaultFormat)})
                    }
                />
                <LazyImage
                    src={BizFileImage}
                    alt={t('title')}
                    className={'plugin-item-image'}
                    wrapperClassName={'plugin-item-image-wrapper'}
                />
                <CardContent sx={{padding: 1}}>
                    <Typography variant={'caption'} component={'p'} marginBlock={3}>
                        {t('description')}
                    </Typography>
                    {
                        !!(data) &&
                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                            <Box sx={{width: '100%', mr: 1}}>
                                <LinearProgress
                                    sx={{borderRadius: 4, height: 10}}
                                    variant={'determinate'}
                                    color={percentageUsed === 100 ? 'warning' : percentageUsed > 100 ? 'error' : 'primary'}
                                    value={Math.min(percentageUsed, 100)}
                                />
                            </Box>
                            <Box sx={{minWidth: 100}}>
                                <Typography variant="caption" color="text.secondary">
                                    {t('usage', {used: Utils.formatBytes(data.partnerDiskUsage), capacity: Utils.formatBytes(data.partnerDiskCapacity)})}
                                </Typography>
                            </Box>
                        </Box>
                    }
                    {
                        !data && !loading &&
                        <Button
                            variant={'contained'}
                            size={'medium'}
                            color={'primary'}
                            sx={{width: '100%'}}
                            onClick={createBizFilePluginData}
                        >
                            {
                                creating
                                    ? 'Creating...'
                                    : 'Create Account'
                            }
                        </Button>
                    }
                </CardContent>
            </Card>
        </>
    );
}


export default usePluginsEndAction;
